import { type VariantProps, cva } from 'class-variance-authority';
import { type ComponentProps, useEffect, useState } from 'react';

import { cn } from '../lib/cn';

/**
 * 학회 마감 카운트다운 — D1 §7-4.
 *
 * 시그니처 요소 중 하나다. 모노스페이스 + extrabold로 "기계가 세고 있다"를 보여준다.
 *
 * 접근성 두 가지가 여기 들어 있다.
 *   1. `prefers-reduced-motion`이면 1초가 아니라 1분마다 갱신하고 초를 숨긴다.
 *      1초마다 바뀌는 숫자는 인지 부담이 크고, 상시 애니메이션에 해당한다.
 *   2. 남은 시간을 색으로만 알리지 않는다. 3일 이내는 접근 이름에 `마감 임박`을 싣는다.
 */
// 굵기 규율(D4-05)에서 700이 최상단이다. 카운트다운은 그 700을 받을 자격이
// 있는 몇 안 되는 요소다 — 화면에서 유일하게 스스로 움직이는 숫자다.
const countdownVariants = cva('font-mono font-bold tabular-nums', {
  variants: {
    urgency: {
      /** 3일 이내 */
      urgent: 'text-seal',
      /** 2주 이내 */
      soon: 'text-gold',
      /** 그 밖 */
      normal: 'text-ink',
      /** 이미 지남 */
      past: 'text-ink-faint',
    },
    size: {
      sm: 'text-sm',
      md: 'text-lg',
      lg: 'text-2xl',
    },
  },
  defaultVariants: { urgency: 'normal', size: 'md' },
});

const MINUTE = 60_000;
const DAY = 86_400_000;

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);

    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

/** 남은 시간을 초 단위 tick으로 흘려보낸다. reduced-motion이면 분 단위. */
function useRemaining(deadline: Date, reduced: boolean) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = reduced ? MINUTE : 1000;
    const id = setInterval(() => setNow(Date.now()), interval);
    return () => clearInterval(id);
  }, [reduced]);

  return deadline.getTime() - now;
}

function urgencyOf(ms: number): 'urgent' | 'soon' | 'normal' | 'past' {
  if (ms <= 0) return 'past';
  if (ms < 3 * DAY) return 'urgent';
  if (ms < 14 * DAY) return 'soon';
  return 'normal';
}

const pad = (n: number) => String(n).padStart(2, '0');

export interface CountdownProps
  extends Omit<ComponentProps<'span'>, 'children'>, VariantProps<typeof countdownVariants> {
  /** 마감 시각. 문자열이면 `new Date()`로 해석 가능한 형식이어야 한다. */
  deadline: Date | string;
  /** 마감이 지난 뒤 보여줄 문구. */
  passedLabel?: string;
  /** `day`면 `D-n`만 보이고 분마다 갱신한다 — 요약 한 줄처럼 초가 판단에 쓰이지 않는 자리. */
  precision?: 'second' | 'day';
}

export function Countdown({
  deadline,
  passedLabel = '마감',
  className,
  size,
  precision = 'second',
  ...props
}: CountdownProps) {
  const target = typeof deadline === 'string' ? new Date(deadline) : deadline;
  const reduced = usePrefersReducedMotion();
  const remaining = useRemaining(target, reduced || precision === 'day');
  const urgency = urgencyOf(remaining);

  if (urgency === 'past') {
    return (
      <span className={cn(countdownVariants({ urgency, size }), className)} {...props}>
        {passedLabel}
      </span>
    );
  }

  const days = Math.floor(remaining / DAY);
  const hours = Math.floor((remaining % DAY) / 3_600_000);
  const minutes = Math.floor((remaining % 3_600_000) / MINUTE);
  const seconds = Math.floor((remaining % MINUTE) / 1000);

  const clock =
    precision === 'day'
      ? null
      : reduced
        ? `${pad(hours)}:${pad(minutes)}`
        : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

  return (
    <span
      // 초마다 읽어주면 소음이 된다. 사용자가 요청할 때만 현재 값을 읽는다.
      role="timer"
      aria-live="off"
      aria-label={`${urgency === 'urgent' ? '마감 임박, ' : ''}마감까지 ${days}일 ${hours}시간 남음`}
      className={cn(countdownVariants({ urgency, size }), className)}
      {...props}
    >
      <span aria-hidden="true">{clock ? `D-${days} ${clock}` : `D-${days}`}</span>
    </span>
  );
}
